"use strict";
// Builds the Overview panel's dynamic content (Profile Header + a single
// Recent Order preview) from real GET /me + GET /me/orders data only — no
// numbers or facts anywhere here are invented. The persistent nav
// rail/mobile-nav (Orders/Downloads/Rewards/Notifications/Security/
// Connected Accounts/Settings/Sign out) is static Liquid markup in
// sections/account-hub.liquid, not rendered from here — per the UI Prompt
// Package's 4.12, Overview is a navigation hub, not an analytics surface:
// no stat-tile grid, no numeric security score.
(function () {
    function escapeHtml(value) {
        var div = document.createElement("div");
        div.textContent = value;
        return div.innerHTML;
    }
    function maskEmail(email) {
        return window.FlixHubFormat.maskEmail(email);
    }
    function maskPhone(phone) {
        var digits = phone.replace(/\s+/g, "");
        if (digits.length <= 4)
            return "***" + digits;
        var prefix = digits.slice(0, digits.length > 8 ? 4 : 2);
        var suffix = digits.slice(-2);
        return prefix + "***" + suffix;
    }
    /** No `isPrimary` concept exists on the backend — this picks the first non-revoked email/phone method as a reasonable stand-in, falling back to naming the provider for anything else (google/discord/passkey identifiers aren't human-readable and must never be shown raw). */
    function primaryIdentifierLine(hubEl, authMethods) {
        var active = authMethods.filter(function (m) {
            return m.revokedAt === null;
        });
        var emailOrPhone = active.find(function (m) {
            return m.type === "email" || m.type === "phone";
        });
        if (emailOrPhone) {
            return emailOrPhone.type === "email"
                ? maskEmail(emailOrPhone.externalIdentifier)
                : maskPhone(emailOrPhone.externalIdentifier);
        }
        var any = active[0];
        if (!any)
            return null;
        var template = hubEl.dataset.i18nConnectedVia || "Connected via %%provider%%";
        var providerLabel = hubEl.dataset["i18nProvider" + capitalize(any.type)] || any.type;
        return template.replace("%%provider%%", providerLabel);
    }
    function capitalize(value) {
        return value.charAt(0).toUpperCase() + value.slice(1);
    }
    function formatMemberSince(hubEl, createdAt, locale) {
        var template = hubEl.dataset.i18nMemberSince || "Member since %%date%%";
        var date;
        try {
            date = new Intl.DateTimeFormat(locale, { year: "numeric", month: "long" }).format(new Date(createdAt));
        }
        catch (err) {
            date = createdAt;
        }
        return template.replace("%%date%%", date);
    }
    /**
     * Whether this account has actually proven an identifier.
     *
     * Read from the real trust level the identity service reports, not from
     * the mere presence of a method: a provider-asserted Google link is
     * `provider_asserted`, which is not the same claim as an address the
     * person demonstrably controls. A revoked method proves nothing either.
     *
     * The reference shows a Verified pill and a badge on the avatar. Both
     * appear only when this is true — there is no decorative version.
     */
    function isVerified(methods) {
        return methods.some(function (m) {
            return m.trustLevel === "verified" && !m.revokedAt;
        });
    }
    var CHECK_MARK = '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12"/></svg>';
    /**
     * Whichever picture this account shows: the upload, the provider's, or the
     * store logo. Empty string when there is none to draw.
     *
     * `default` resolves against data-default-avatar-url rather than a URL
     * from the backend, because the logo is a theme asset and its path is a
     * theme setting.
     */
    function avatarImageHtml(hubEl, profile) {
        var avatar = window.FlixHubFormat.resolvedAvatar(profile);
        var url = avatar.source === "default" ? hubEl.dataset.defaultAvatarUrl || "" : avatar.url || "";
        if (!url)
            return "";
        return ('<img data-flix-overview-avatar-image data-avatar-source="' +
            escapeHtml(avatar.source) +
            '" alt="" src="' +
            escapeHtml(url) +
            '" style="position:absolute;inset:0;width:100%;height:100%;object-fit:cover;border-radius:50%">');
    }
    function profileHeaderHtml(hubEl, profile, locale) {
        var initials = window.FlixHubFormat.initials(profile.displayName);
        var name = profile.displayName || hubEl.dataset.i18nUnnamed || "Flix Member";
        var identifier = primaryIdentifierLine(hubEl, profile.authMethods);
        var memberSince = formatMemberSince(hubEl, profile.createdAt, locale);
        var verified = isVerified(profile.authMethods);
        var verifiedPill = verified
            ? '<span data-flix-overview-verified style="display:inline-flex;align-items:center;gap:5px;flex:none;padding:4px 10px;border-radius:var(--radius-lg);background:color-mix(in srgb, var(--success) 12%, transparent);border:1px solid color-mix(in srgb, var(--success) 38%, transparent);color:color-mix(in srgb, var(--success) 78%, #ffffff);font-size:12px;font-weight:700">' +
                CHECK_MARK +
                escapeHtml(hubEl.dataset.i18nEmailVerified || "Verified") +
                "</span>"
            : "";
        // The same fact, drawn on the avatar the way the reference draws it.
        var avatarBadge = verified
            ? '<span aria-hidden="true" style="position:absolute;inset-inline-end:-1px;bottom:-1px;width:20px;height:20px;border-radius:50%;background:var(--success);border:2px solid var(--bg2);display:flex;align-items:center;justify-content:center;color:color-mix(in srgb, var(--success) 22%, #000000)">' +
                CHECK_MARK +
                "</span>"
            : "";
        var restrictedNotice = profile.status === "restricted"
            ? '<div style="margin-top:10px;display:inline-flex;align-items:center;gap:6px;padding:5px 10px;border-radius:var(--radius-lg);background:rgba(245,166,35,.14);border:1px solid rgba(245,166,35,.4);color:var(--warning);font-size:12px;font-weight:700">' +
                escapeHtml(hubEl.dataset.i18nStatusRestricted || "Restricted account") +
                "</div>"
            : "";
        return ('<div style="display:flex;align-items:center;gap:16px">' +
            '<span style="position:relative;flex:none;display:inline-flex">' +
            // The gradient-and-initials circle is the design's avatar; whichever
            // picture applies covers it rather than replacing it, so a failed load
            // — a provider CDN having a moment, a logo asset that moved — uncovers
            // the intended rendering instead of an empty disc.
            '<span aria-hidden="true" style="position:relative;overflow:hidden;width:62px;height:62px;border-radius:50%;background:linear-gradient(135deg,var(--accent),var(--accent2));display:flex;align-items:center;justify-content:center;font-family:var(--display);font-weight:800;font-size:23px;color:#160d05;box-shadow:0 8px 22px -8px var(--glow)">' +
            escapeHtml(initials) +
            avatarImageHtml(hubEl, profile) +
            "</span>" +
            avatarBadge +
            "</span>" +
            '<div style="min-width:0">' +
            '<div style="display:flex;align-items:center;gap:9px;flex-wrap:wrap">' +
            '<div data-flix-overview-name style="font-family:var(--display);font-weight:800;font-size:22px;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' +
            escapeHtml(name) +
            "</div>" +
            verifiedPill +
            "</div>" +
            // Identifier and member-since share a line, as the reference sets
            // them, and wrap onto two only when the width will not take both.
            '<div style="font-size:14px;color:var(--dim);margin-top:4px">' +
            (identifier ? escapeHtml(identifier) + '<span style="opacity:.5"> · </span>' : "") +
            escapeHtml(memberSince) +
            "</div>" +
            restrictedNotice +
            "</div>" +
            "</div>");
    }
    /** The design's section label: mono 10px, 2px tracking, --faint. */
    function sectionLabelHtml(label) {
        return ('<div style="font-family:var(--mono);font-size:10px;letter-spacing:2px;color:var(--accent);margin:26px 0 10px;text-transform:uppercase">' +
            escapeHtml(label) +
            "</div>");
    }
    function recentOrderLoadingHtml() {
        return ('<div data-flix-overview-recent-order style="height:78px;border-radius:14px;background:var(--surface);border:1px solid var(--border);display:flex;align-items:center;padding:14px;gap:13px">' +
            '<span style="width:34px;height:34px;border-radius:10px;background:var(--surface2);animation:flx-hub-spin 1.4s linear infinite;border:2px solid var(--border);border-top-color:var(--accent)"></span>' +
            "</div>");
    }
    /** The same package mark the Orders panel's own empty state uses. */
    var PACKAGE_ICON = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/><path d="M3.3 7 12 12l8.7-5"/><path d="M12 22V12"/></svg>';
    /**
     * What the Recent order section shows when the account has none.
     *
     * Previously the slot was simply removed, which left the panel as a name
     * and then several hundred pixels of nothing — the section vanished
     * rather than reporting that it was empty.
     *
     * Nothing is invented here. The card keeps the exact chrome of the order
     * card it stands in for — same surface, same border, same 14px radius —
     * and the strings are the ones the Orders panel already shows for this
     * same situation, so the two screens say the same thing in the same voice
     * and in whichever language the store is serving.
     *
     * It is composed here rather than through `FlixHubRender.emptyState`. That
     * component is sized for a whole panel: its `clamp(40px,8vw,72px)` padding
     * produced a 285px block standing in for a 78px order card, which is not a
     * preview but a billboard. This keeps the component's own parts — the
     * circular icon well, the display-weight title, the --dim message — at the
     * card's scale instead.
     *
     * Deliberately no call to action: the Orders panel one screen away already
     * carries "Browse the store", and a link out of the storefront does not
     * belong inside a preview.
     */
    /**
     * What the section shows when the order could not be read.
     *
     * It used to delete itself, for a good reason that has since stopped
     * applying: a panel-level error state stacked under a working profile
     * header would have claimed the whole screen had failed, when only one
     * section had. But deleting it left exactly the hole the empty state
     * was added to close. This is the third thing the same card can say —
     * same chrome, same scale — and it offers the retry the Hub already
     * knows how to handle.
     */
    function recentOrderErrorHtml(hubEl) {
        return (sectionLabelHtml(hubEl.dataset.i18nRecentOrder || "Recent order") +
            '<div data-flix-overview-recent-order data-flix-overview-error ' +
            'style="border-radius:14px;background:var(--surface);border:1px solid var(--border);' +
            'padding:20px;display:flex;align-items:center;gap:13px;flex-wrap:wrap">' +
            '<span aria-hidden="true" style="width:38px;height:38px;flex:none;border-radius:11px;background:var(--surface2);' +
            'display:flex;align-items:center;justify-content:center;color:var(--warning)">' +
            ALERT_ICON +
            "</span>" +
            '<span style="flex:1;min-width:140px;font-size:13px;color:var(--dim);line-height:1.5">' +
            escapeHtml(hubEl.dataset.i18nErrorMessage || "We couldn't load your account. Please try again.") +
            "</span>" +
            '<button type="button" data-flix-hub-retry style="flex:none;padding:9px 18px;border-radius:var(--radius-button);' +
            'border:1px solid var(--border);background:transparent;color:var(--text);font-family:var(--display);' +
            'font-weight:700;font-size:13px;cursor:pointer" style-hover="border-color:var(--borderHi)">' +
            escapeHtml(hubEl.dataset.i18nRetry || "Retry") +
            "</button>" +
            "</div>");
    }
    var ALERT_ICON = '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v6"/><path d="M12 16h.01"/></svg>';
    function recentOrderEmptyHtml(hubEl) {
        return (sectionLabelHtml(hubEl.dataset.i18nRecentOrder || "Recent order") +
            '<div data-flix-overview-recent-order data-flix-overview-empty ' +
            'style="border-radius:14px;background:var(--surface);border:1px solid var(--border);' +
            'padding:22px 20px;display:flex;flex-direction:column;align-items:center;justify-content:center;' +
            'gap:10px;text-align:center">' +
            '<span aria-hidden="true" style="width:44px;height:44px;border-radius:50%;background:var(--surface2);' +
            'border:1px solid var(--border);display:flex;align-items:center;justify-content:center;color:var(--faint)">' +
            PACKAGE_ICON +
            "</span>" +
            '<div style="font-family:var(--display);font-weight:800;font-size:15px;color:var(--text)">' +
            escapeHtml(hubEl.dataset.i18nEmptyTitle || "No orders yet") +
            "</div>" +
            '<div style="font-size:13px;line-height:1.6;color:var(--dim);max-width:280px">' +
            escapeHtml(hubEl.dataset.i18nEmptyMessage ||
                "Your orders will show up here once you make a purchase.") +
            "</div>" +
            "</div>");
    }
    /**
     * The reference draws an order's state as a tinted pill, not as text
     * trailing the date. The hue carries the meaning: settled states read
     * green, in-flight amber, reversed red, and anything unrecognised stays
     * neutral rather than being forced into a colour it has not earned.
     *
     * There is deliberately no source/type badge beside it. The reference
     * shows one (SOURCE, PSD, AFTER EFFECTS) but `FlixOrderListItem` has no
     * such field — no product format crosses the wire — so drawing one would
     * mean inventing a fact about the order.
     */
    function statusToneFor(status) {
        var key = status.toLowerCase();
        if (/fulfil|deliver|paid|complete/.test(key)) {
            return {
                fg: "color-mix(in srgb, var(--success) 78%, #ffffff)",
                bg: "color-mix(in srgb, var(--success) 12%, transparent)",
                border: "color-mix(in srgb, var(--success) 38%, transparent)",
            };
        }
        if (/refund|cancel|void|fail/.test(key)) {
            return { fg: "#f2837a", bg: "rgba(224,86,74,.12)", border: "rgba(224,86,74,.38)" };
        }
        if (/pend|process|partial|progress|unfulfil|authoriz|authoris/.test(key)) {
            return { fg: "#f5a623", bg: "rgba(245,166,35,.12)", border: "rgba(245,166,35,.38)" };
        }
        return { fg: "var(--dim)", bg: "rgba(255,255,255,.05)", border: "var(--border)" };
    }
    function statusPillHtml(label) {
        var tone = statusToneFor(label);
        return ('<span data-flix-order-status style="display:inline-flex;align-items:center;flex:none;padding:4px 11px;border-radius:var(--radius-lg);font-size:12px;font-weight:700;color:' +
            tone.fg +
            ";background:" +
            tone.bg +
            ";border:1px solid " +
            tone.border +
            '">' +
            escapeHtml(label) +
            "</span>");
    }
    function recentOrderCardHtml(hubEl, order, locale) {
        var title = order.primaryLineItemTitle || order.name;
        var date = window.FlixHubFormat.date(order.processedAt, locale);
        var status = window.FlixHubFormat.humanizeStatus(order.fulfillmentStatus) ||
            window.FlixHubFormat.humanizeStatus(order.financialStatus);
        var thumb = order.primaryLineItemImageUrl
            ? '<img src="' +
                escapeHtml(order.primaryLineItemImageUrl) +
                '" alt="" style="width:44px;height:44px;border-radius:10px;object-fit:cover;flex:none">'
            : '<span style="width:44px;height:44px;border-radius:10px;background:var(--surface2);display:flex;align-items:center;justify-content:center;flex:none;color:var(--faint)"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg></span>';
        return (sectionLabelHtml(hubEl.dataset.i18nRecentOrder || "Recent order") +
            '<button type="button" data-flix-hub-nav="orders" data-flix-overview-recent-order style="width:100%;text-align:start;display:flex;align-items:center;gap:13px;padding:14px;border-radius:14px;background:var(--surface);border:1px solid var(--border);cursor:pointer;transition:border-color .2s,transform .2s" style-hover="border-color:var(--borderHi);transform:translateY(-2px)">' +
            thumb +
            '<div style="min-width:0;flex:1">' +
            '<div style="font-size:14px;font-weight:700;color:var(--text);white-space:nowrap;overflow:hidden;text-overflow:ellipsis">' +
            escapeHtml(title) +
            "</div>" +
            '<div style="font-size:12px;color:var(--dim);margin-top:2px">' +
            escapeHtml(date) +
            "</div>" +
            "</div>" +
            // Price above status, end-aligned, as the reference stacks them. The
            // price is plain --text: the accent is the Hub's navigation colour,
            // and using it here made every order look like a link.
            '<div style="flex:none;display:flex;flex-direction:column;align-items:flex-end;gap:6px">' +
            '<div data-flix-order-price style="font-family:var(--display);font-weight:800;font-size:14px;color:var(--text)">' +
            escapeHtml(window.FlixHubFormat.money(order.totalPrice, locale)) +
            "</div>" +
            (status ? statusPillHtml(status) : "") +
            "</div>" +
            "</button>");
    }
    async function render(hubEl, contentEl, profile) {
        var locale = document.documentElement.lang || "en";
        contentEl.innerHTML =
            profileHeaderHtml(hubEl, profile, locale) +
                recentOrderLoadingHtml();
        try {
            var page = await window.FlixAuthClient.listOrders({ limit: 1 });
            var slot = contentEl.querySelector("[data-flix-overview-recent-order]");
            if (!slot)
                return;
            var firstOrder = page.orders[0];
            if (firstOrder) {
                slot.outerHTML = recentOrderCardHtml(hubEl, firstOrder, locale);
            }
            else {
                // An account with no orders is a state to report, not a section to
                // delete — removing it left the panel visibly unfinished.
                slot.outerHTML = recentOrderEmptyHtml(hubEl);
            }
        }
        catch (err) {
            // Still not a panel-level error — the profile header above it loaded
            // fine and the screen has not failed. The section reports its own
            // failure, in its own card, and offers a retry.
            var failedSlot = contentEl.querySelector("[data-flix-overview-recent-order]");
            if (failedSlot)
                failedSlot.outerHTML = recentOrderErrorHtml(hubEl);
            window.flixCompileHoverStyles(contentEl);
        }
    }
    window.FlixHubOverview = { render: render };
})();
